/**
 * useTaskNotifications.js
 * 
 * Hook que escuta Supabase Realtime na tabela `tasks`.
 * Quando chega tarefa atribuída ao seller logado (e criada por outro),
 * dispara push notification no navegador.
 * 
 * Localização: src/hooks/useTaskNotifications.js
 */

import { useEffect, useRef } from 'react';
import { supabase } from '../services/supabase';

const CRM_API = process.env.REACT_APP_CRM_API_URL || 'http://localhost:5000/api/crm';

export function useTaskNotifications(sellerId) {
  const sellersRef = useRef([]);

  // Buscar sellers uma vez (para mostrar nome do criador na notificação)
  useEffect(() => {
    if (!sellerId) return;
    fetch(`${CRM_API}/sellers`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { sellersRef.current = data; })
      .catch(() => {});
  }, [sellerId]);

  // Pedir permissão de notificação
  useEffect(() => {
    if (!sellerId) return;
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [sellerId]);

  // Escutar Realtime
  useEffect(() => {
    if (!sellerId || !supabase) return;

    const channel = supabase
      .channel('tasks-notifications')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'tasks' },
        (payload) => {
          const task = payload.new;

          // Só notifica se a tarefa é para mim E foi criada por outro
          if (task.assigned_to !== sellerId) return;
          if (task.created_by === sellerId) return;

          // Nome do criador
          const creator = sellersRef.current.find(s => s.id === task.created_by);
          const creatorName = creator ? creator.name.split(' ')[0] : 'Alguém';

          // Dispara push notification
          if (Notification.permission === 'granted') {
            const notification = new Notification('📋 Nova tarefa para você', {
              body: `${creatorName}: ${task.title}`,
              icon: '/favicon.ico',
              tag: `task-${task.id}`,
              requireInteraction: true,
            });

            notification.onclick = () => {
              window.focus();
              window.location.href = '/app/tarefas';
              notification.close();
            };
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sellerId]);
}
